/**
 * The lifetime of a template's parsed `<template>`.
 *
 * A template's static HTML has to become nodes before anything can be mounted,
 * and the parser is what does it. The question this module owns is what happens
 * to that parse afterwards: whether it is kept as a prototype to clone, or
 * handed over to the mount that asked for it and never rebuilt.
 *
 * The price is exact and worth stating once. A parsed `<template>` holding K
 * nodes retains **K + 2** — the element and its content fragment — plus one
 * contents-owner `HTMLDocument` that Blink creates the first time any template
 * is parsed in a document and never releases (measured: a bare page reads
 * Nodes=4 / Documents=1, one held template Nodes=12 / Documents=2, and dropping
 * every reference returns Nodes to 5, not 4, with Documents still 2). Cloning
 * pays that back per INSTANTIATION, so a template instantiated once never
 * amortises it at all.
 *
 * Hence the rule, and its two deliberate limits:
 *
 * - **The first instantiation consumes its parse.** Its nodes BECOME the live
 *   tree instead of a copy of a prototype nothing has asked to clone yet. On
 *   the react-comparison page `App` and `Controls` are instantiated exactly
 *   once for the life of the document and were retaining 35 nodes between them
 *   for that never-arriving amortisation; consuming is also ~3x cheaper there
 *   than parse-then-clone (2.10 vs 5.95 us for `App`, 1.95 vs 6.35 for
 *   `Controls`, 200 distinct single-use templates per round). Retained nodes
 *   after 5 create/clear cycles: 105 -> 69 (#1375).
 * - **A caller that already knows better says so.** A keyed repeat's row is a
 *   declaration that the second instantiation is on its way, so it caches on
 *   the first rather than paying a parse it will throw away. Without that,
 *   cold `create1k` paid +0.207 ms [+0.110, +0.275] (n=100) for one discarded
 *   parse and recovered no nodes for it.
 * - **The parse is never dropped once built.** Releasing it when the last
 *   instance unmounts recovers the row template's 11 retained nodes on an
 *   emptied page; #1355 costed that re-parse at ~6 us per create cycle, 0.07%.
 *   Measured on the benchmark's own window, arms interleaved in one browser
 *   process: **+0.198 ms [+0.127, +0.242] warm create1k at two parses per
 *   cycle, and +0.225 ms [+0.185, +0.260] at one** — the cost does not track
 *   the parse count, because a parse is 5.00 us and cloning from a fresh parse
 *   is only 0.015 ms/1000 slower than from a held one. It is the price of
 *   discarding and rebuilding a cache inside a hot window, and it is ~30x the
 *   projection either way.
 */
/**
 * The compiled `<template>` is memoized on the Template, but it belongs to the
 * document that parsed it. A realm can hold more than one — an iframe, a popup
 * opened by the page, `document.implementation.createHTMLDocument`, or a test
 * DOM swapped in mid-process — and cloning a foreign document's node into
 * another document's tree corrupts that document's internals (#1244). The memo
 * is therefore valid only while its owner is still the current document; any
 * other document recompiles its own.
 */
function currentDomTemplate(template) {
    const cached = template.domTemplate;
    if (!cached)
        return null;
    // With no current document there is nothing to disagree with: keep the memo
    // rather than recompiling into a `createTemplate` that would throw RT401.
    const documentObj = globalThis.document;
    return !documentObj || cached.ownerDocument === documentObj ? cached : null;
}
/**
 * The memoized parse, built and cached if it does not exist yet.
 *
 * This is the HYDRATION entry point, and hydration is why it still exists in
 * this unconditional form: adopting server DOM walks the parsed template
 * alongside the markup, so it needs the parse intact rather than consumed, and
 * a page that hydrates has already paid for the markup it is claiming.
 */
export function ensureDomTemplate(template, domAdapter) {
    const cached = currentDomTemplate(template);
    if (cached)
        return cached;
    const element = domAdapter.createTemplate(template.staticHtml, template.namespace);
    template.domTemplate = element;
    return element;
}
/**
 * The parse one mount will build from, caching it only if it will be cloned
 * more than once.
 *
 * `reused` is the caller declaring this instantiation to be one of a set — a
 * keyed repeat's row. Everything else is a first instantiation until a second
 * live one proves otherwise, and a first instantiation consumes.
 *
 * Returned rather than kept internal because the caller needs the element
 * itself: it is the last-resort owner document for the nodes the mount will
 * create, and a consumed `content` fragment cannot stand in for it — the
 * fragment belongs to the inert contents document, which is exactly the
 * foreign-document confusion #1244 is about.
 */
export function ensureParse(template, domAdapter, reused) {
    const cached = currentDomTemplate(template);
    if (cached)
        return cached;
    const element = domAdapter.createTemplate(template.staticHtml, template.namespace);
    // Assigned on both branches rather than left alone: a memo from another
    // document (#1244) has to be dropped here too.
    template.domTemplate = template.liveInstances || reused ? element : null;
    return element;
}
/**
 * The fragment this mount owns: a clone of the parse, or the parse itself.
 *
 * `ensureParse` has already recorded which it is by whether it cached `source`,
 * so the two cannot disagree — an uncached parse is one nothing will ever clone
 * again, and handing it over is what makes a single-use template retain
 * nothing at all.
 */
export function takeFragment(template, source) {
    return template.domTemplate === source
        ? source.content.cloneNode(true)
        : source.content;
}
//# sourceMappingURL=render-dom-parse.js.map